import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/**
 * Collapses the pre-selection price range to the concrete variant price once
 * the shopper makes a selection. snippets/price.liquid renders the range state
 * visible and the selected state hidden alongside it; every variant:update
 * swaps their visibility. Runs at the document level so it fires after the
 * section-scoped listeners (ProductPrice, sticky add-to-cart) have replaced
 * their markup with the freshly rendered HTML — which arrives in the range
 * state again.
 *
 * Scoped to the card when the event originates from a product card, so a card
 * selection never collapses the ranges of its section siblings.
 */
function collapsePriceRanges(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const scope =
    target.closest('custom-product-card, product-card') || target.closest('.shopify-section, dialog') || document;

  for (const container of scope.querySelectorAll('[ref="priceContainer"]')) {
    const range = container.querySelector('[data-price-range]');
    const selected = container.querySelector('[data-price-selected]');
    if (!range || !selected) continue;

    range.setAttribute('hidden', '');
    selected.removeAttribute('hidden');
  }
}

document.addEventListener(ThemeEvents.variantUpdate, collapsePriceRanges);
